import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { sellTicketStatus, sellTicketStatusInversed, SellTicketStatusKeys } from '@teinor/erp/types/company/order/sell/sellTicket'
import { SellTicketStatusPercentageData, SellTicketStatusPercentageDataKeys } from './types'
import './sellticketstatuspercentatge.sass'

interface DSBSellTicketStatusPercentageProps extends DSBSellTicketStatusPercentageLogicProps {
    title?: string
}

const DSBSellTicketStatusPercentage = ({ title, end_date, start_date }: DSBSellTicketStatusPercentageProps) => {
    const { t } = useTwinTranslation()
    const { sellTickets } = useDSBSellTicketStatusPercentageLogic({ end_date, start_date })
    const { arrMonthShort } = useDictMonths()
    const customlegendFormatter = (name: any) => {
        return `<div class='legend-label'>${name}</div>`
    }
    const series = [
        {
            name: t('reserved', 'Reservado'),
            type: 'column',
            data: sellTickets.saved
        },
        {
            name: t('accepted', 'Aceptados'),
            type: 'column',
            data: sellTickets.accepted
        },
        {
            name: t('refunded', 'Devueltos'),
            type: 'column',
            data: sellTickets.refunded
        },
    ]
    return (
        <div className='flex flex-col flex-auto h-1 gap-10 sell_ticket_status_percentage'>
            <h3>{title}</h3>
            <div className='flex-auto h-1'>
                <Chart options={{
                    chart: {
                        type: 'bar',
                        stacked: true,
                        events: {
                            mounted: (chart) => {
                                chart.windowResizeHandler()
                            }
                        },
                        toolbar: {
                            show: false
                        },
                        zoom: {
                            enabled: true
                        }
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: '100%',
                        },
                    },
                    stroke: {
                        show: true,
                        width: 2,
                        colors: ['transparent']
                    },
                    yaxis: {
                        labels: {
                            style: {
                                colors: '#8F9BA4',
                                fontFamily: 'Roboto'
                            }
                        },
                        max: 100,
                    },
                    xaxis: {
                        labels: {
                            style: {
                                fontFamily: 'Roboto',
                                colors: '#8F9BA4',
                            }
                        },
                        categories: arrMonthShort
                    },
                    fill: {
                        opacity: 1,
                    },
                    legend: {
                        position: 'top',
                        horizontalAlign: 'left',
                        formatter: customlegendFormatter,
                        fontFamily: 'Roboto',
                    },
                    colors: ['#e8cf30', '#1DB27B', '#EB2D53'],
                    tooltip: {
                        y: {
                            formatter: function (val) {
                                return val + "%"
                            }
                        }
                    }
                }} series={series} type="bar" height={'100%'} />
            </div>
        </div>
    )
}

interface DSBSellTicketStatusPercentageLogicProps {
    start_date: Date
    end_date: Date
}

const useDSBSellTicketStatusPercentageLogic = ({ end_date, start_date }: DSBSellTicketStatusPercentageLogicProps) => {
    const [sellTickets, setSellTickets] = useState<SellTicketStatusPercentageData>({ saved: [], accepted: [], refunded: [] })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getAllSellTicketsForStats', {
            attributes: ['status', 'createdAt'],
            where: {
                'SellTicket.createdAt': { '$betweenN': [start_date, end_date] }, status: { '$notN': sellTicketStatusInversed['creating'] }, is_last: true
            }
        })
        const totals = Array(12).fill(0)
        const mySellTicketStatus: SellTicketStatusPercentageData = { saved: Array(12).fill(0), accepted: Array(12).fill(0), refunded: Array(12).fill(0) }

        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                totals[month] += 1
                let myStatusInversed = sellTicketStatus?.[element.status as SellTicketStatusKeys] 
                myStatusInversed = (myStatusInversed === 'partialCharged') ? 'saved' : myStatusInversed
                myStatusInversed = (myStatusInversed === 'partialRefunded' || myStatusInversed === 'canceled') ? 'refunded' : myStatusInversed
                mySellTicketStatus[myStatusInversed as SellTicketStatusPercentageDataKeys][month] += 1
            }
            for (let key in totals) {
                mySellTicketStatus.saved[key] = mySellTicketStatus.saved[key] ? parseInt(String((mySellTicketStatus.saved[key] / totals[key]) * 100)) : 0
                mySellTicketStatus.accepted[key] = mySellTicketStatus.accepted[key] ? parseInt(String((mySellTicketStatus.accepted[key] / totals[key]) * 100)) : 0
                mySellTicketStatus.refunded[key] = mySellTicketStatus.refunded[key] ? parseInt(String((mySellTicketStatus.refunded[key] / totals[key]) * 100)) : 0
            }
            setSellTickets(mySellTicketStatus)
        }
    }, [setSellTickets, end_date, start_date])

    useEffect(() => {
        getData()
    }, [getData])

    return { sellTickets }
}

export default DSBSellTicketStatusPercentage